/**
 * Comparación visual: anuncio Fotocasa (original) vs datos importados al modelo canónico (mismas claves que Idealista v2).
 */
import { useState, useCallback } from "react";
import { useTheme } from "../components/ThemeProvider";
import { portalImportOne, mensajeErrorPortalImport } from "../services/portalImportApi";
import usePermiso from "../hooks/usePermiso";

const EJEMPLO_URL =
  "https://www.fotocasa.es/es/comprar/vivienda/denia/aire-acondicionado-calefaccion-parking-jardin-terraza-no-amueblado/189316209/d";

/** Debe coincidir con CANONICAL_KEYS en backend `portal-fotocasa-v2.service.js`. */
const FOTOCASA_V2_CANONICAL_KEYS = [
  "titulo",
  "web_titulo",
  "operacion",
  "tipo_inmueble_id",
  "tipo_inmueble_nombre",
  "precio_venta",
  "web_desc_larga_es",
  "num_dormitorios",
  "num_banos",
  "num_terrazas",
  "num_balcones",
  "m2_construidos",
  "m2_utiles",
  "m2_parcela",
  "m2_terrazas",
  "m2_jardin",
  "m2_jardin_obs",
  "codigo_postal",
  "nombre_via",
  "numero",
  "poblacion_id",
  "poblacion_nombre",
  "referencia",
  "referencia_portal",
  "alias",
  "cal_energetica",
  "cal_energetica_consumo",
  "cal_energetica_emisiones",
  "orientacion_id",
  "orientacion_nombre",
  "plantas_inmueble",
  "altura_planta",
  "piso",
  "fecha_actualizacion",
  "ano_construccion",
  "tipo_anunciante",
  "telefono",
  "eq_piscina_priv",
  "eq_piscina_comun",
  "eq_garaje",
  "eq_calefaccion",
  "eq_calefaccion_id",
  "eq_aire",
  "eq_ascensor",
  "eq_trastero",
  "eq_barbacoa",
  "eq_lavadero",
  "eq_chimenea",
  "eq_padel",
  "eq_gimnasio",
  "eq_tenis",
  "eq_agua",
  "eq_agua_riego",
  "eq_gas",
  "eq_luz",
  "eq_zona_infantil",
  "eq_puerta_blindada",
  "eq_alarma",
  "ub_primera_linea",
  "ub_segunda_linea",
  "ub_vistas_id",
  "amueblado",
  "exterior",
  "web_url",
  "precio_rebajado",
  "gastos_comunidad",
];

const FOTOCASA_PORTAL_EXTRAS_ROWS = [
  "estado_inmueble_portal",
  "antiguedad_portal",
  "promocion_padre_nombre_portal",
  "suite_portal",
  "patio_portal",
  "patio_tipo_portal",
  "trastero_inferido_descripcion",
  "vistas_portal",
  "piscina_dimensiones_portal",
  "terrazas_detalle_portal",
  "telefono_portal_raw",
  "zona_via_portal",
  "agua_caliente_portal",
  "eq_agua_obs",
  "chimenea_preinstalacion_portal",
  "m2_sotano_portal",
  "bajada_precio_texto",
  "videoportero_portal",
  "acceso_privado_playa_portal",
  "planta_portal_resumen",
  "dormitorios_detalle_portal",
  "piscinas_comun_num_portal",
  "terrazas_desglose_portal",
  "calefaccion_energia_portal",
  "calefaccion_nombre",
  "features_raw_portal",
  "dynamic_features_portal",
  "tipo_cocina_portal",
  "municipio_portal",
  "provincia_portal",
  "referencia_comercial_portal",
  "fotocasa_ad_id",
  "fecha_publicacion_iso_portal",
  "caracteristicas_portal",
];

function normalizarTelefonoEs(raw) {
  if (raw == null || raw === "") return null;
  const d = String(raw)
    .replace(/\D/g, "")
    .replace(/^34/, "");
  return d.length >= 9 ? d.slice(-9) : null;
}

const IMG_PROXY_BASE =
  window.location.hostname === "localhost"
    ? "http://localhost:4000/api/img-proxy?url="
    : "https://vitalcasa-crm-production.up.railway.app/api/img-proxy?url=";

function proxyFoto(u) {
  if (!u || typeof u !== "string") return u;
  if (u.startsWith("data:")) return u;
  let url = u.trim();
  if (url.startsWith("//")) url = `https:${url}`;
  else if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  // Fotocasa CDN: carga directa
  if (/fotocasa\.es|static\.fotocasa|images\.fotocasa/i.test(url)) return url;
  return IMG_PROXY_BASE + encodeURIComponent(url);
}

function fmtValor(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  if (typeof v === "object") return JSON.stringify(v, null, 0);
  return String(v);
}

const FOTOS_UI_MAX = 50;

function compactarTituloSeoHabitacliaMostrar(raw) {
  if (!raw || typeof raw !== "string") return "";
  let words = raw.replace(/\s+/g, " ").trim().split(/\s+/);
  if (words.length < 10) return raw.replace(/\s+/g, " ").trim();
  const minRun = 4;
  const maxRun = 24;
  let guard = 0;
  while (guard++ < 80 && words.length >= minRun * 2) {
    let removed = false;
    outer: for (let n = Math.min(maxRun, words.length - 1); n >= minRun; n--) {
      for (let i = 0; i <= words.length - n; i++) {
        const run = words.slice(i, i + n).join(" ").toLowerCase();
        for (let j = i + n; j <= words.length - n; j++) {
          if (words.slice(j, j + n).join(" ").toLowerCase() === run) {
            words = [...words.slice(0, j), ...words.slice(j + n)];
            removed = true;
            break outer;
          }
        }
      }
    }
    if (!removed) break;
  }
  return words.join(" ").replace(/\s+/g, " ").trim();
}

function tituloResumenLegible(snap) {
  const raw = (snap.titulo || snap.web_titulo || "").trim();
  if (!raw) return "—";
  return compactarTituloSeoHabitacliaMostrar(raw).slice(0, 400);
}

function fmtValorSnapshotCampo(k, snap, extras, snapReferencia, telefonoResuelto) {
  if (k === "referencia") return fmtValor(snapReferencia);
  if (k === "telefono") return fmtValor(telefonoResuelto);
  if (k === "titulo" || k === "web_titulo") return tituloResumenLegible(snap);
  if (k === "bajada_precio_euros") return fmtPrecio(snap[k]);
  if (k === "cal_energetica_consumo") return snap[k] != null ? `${snap[k]} kWh/m²/año` : "—";
  if (k === "cal_energetica_emisiones") return snap[k] != null ? `${snap[k]} kg CO₂/m²/año` : "—";
  return fmtValor(snap[k]);
}

function fmtPrecio(n) {
  if (n == null || n === "") return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n);
  return new Intl.NumberFormat("es-ES").format(x) + " €";
}

function fmtFechaHora(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CompararPortalFotocasaV2() {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const tx = t.textPrimary;
  const surface = t.bgCard;
  const { puede } = usePermiso();
  const verPagina = puede("cmp_fotocasa", "ver");
  const [url, setUrl] = useState(EJEMPLO_URL);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);

  const ejecutar = useCallback(async () => {
    setErr(null);
    setData(null);
    const u = url.trim();
    if (!u || !/fotocasa\.es/i.test(u)) {
      setErr("Pega una URL completa de una ficha de fotocasa.es");
      return;
    }
    setLoading(true);
    try {
      const res = await portalImportOne("/portal-v2/fotocasa/import-one", { url: u });
      setData(res);
    } catch (e) {
      setErr(mensajeErrorPortalImport(e));
    } finally {
      setLoading(false);
    }
  }, [url]);

  const abrirOriginalNuevaPestaña = () => {
    const u = (data?.url_origen || url).trim();
    if (u) window.open(u, "_blank", "noopener,noreferrer");
  };

  const abrirOriginalPopup = () => {
    const u = (data?.url_origen || url).trim();
    if (!u) return;
    const w = Math.min(980, window.screen.width - 80);
    const h = Math.min(1100, window.screen.height - 80);
    const left = window.screenX + window.outerWidth - w - 24;
    const top = window.screenY + 40;
    window.open(
      u,
      "fotocasa_comparar",
      `noopener,noreferrer,width=${w},height=${h},left=${left},top=${top},scrollbars=yes`
    );
  };

  const snap = data?.snapshot || {};
  const extras = data?.portal_extras || {};
  /** Referencia comercial de agencia; no confundir con referencia_portal (id del portal). */
  const snapReferencia = snap.referencia ?? extras.referencia_comercial_portal ?? null;
  const telefonoResuelto =
    snap.telefono ??
    data?.telefono ??
    normalizarTelefonoEs(extras.telefono_anuncio) ??
    normalizarTelefonoEs(data?.telefono_raw) ??
    null;
  /** Incluye `referencia` y `telefono` para que siempre salgan filas en la tabla (valor → "—" si null). */
  const snapKeys = [...new Set([...FOTOCASA_V2_CANONICAL_KEYS, ...Object.keys(snap), "referencia", "telefono"])].sort();
  const fotosRaw = Array.isArray(data?.fotos_urls) ? data.fotos_urls : [];
  const fotos = (() => {
    const keys = new Set();
    const out = [];
    for (const u of fotosRaw) {
      if (!u || typeof u !== "string") continue;
      const k = u.split("?")[0].toLowerCase();
      if (!k || keys.has(k)) continue;
      keys.add(k);
      out.push(u);
    }
    return out;
  })();
  const videosRaw = Array.isArray(data?.videos_urls) ? data.videos_urls : [];
  const videos = (() => {
    const keys = new Set();
    const out = [];
    for (const u of videosRaw) {
      if (!u || typeof u !== "string") continue;
      const k = u.split("?")[0].toLowerCase();
      if (!k || keys.has(k)) continue;
      keys.add(k);
      out.push(u);
    }
    return out;
  })();
  const youtubeThumb = (url) => {
    const m = String(url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{8,15})/i);
    return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
  };
  const caraPortal = extras.caracteristicas_portal || {};
  const m2ParcelaResuelto = snap.m2_parcela ?? caraPortal.m2_terreno ?? null;
  const piscinaResuelta =
    snap.eq_piscina_priv === true ||
    caraPortal.piscina === true ||
    snap.eq_piscina_comun === true ||
    caraPortal.piscina_comun === true;
  const garajeTiene = snap.eq_garaje === true || caraPortal.garaje === true;
  const garajeNo = snap.eq_garaje === false || caraPortal.garaje === false;
  const aireTiene = snap.eq_aire === true || caraPortal.aire_acondicionado === true;
  const aireNo = snap.eq_aire === false || caraPortal.aire_acondicionado === false;
  const calefTiene = snap.eq_calefaccion === true || caraPortal.calefaccion === true;
  const calefNo = snap.eq_calefaccion === false || caraPortal.calefaccion === false;
  const descLen = snap.web_desc_larga_es ? String(snap.web_desc_larga_es).length : 0;

  const card = {
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    background: surface,
    padding: 16,
    marginBottom: 16,
  };

  if (!verPagina) {
    return (
      <div style={{ padding: 24, color: t.textMuted, fontSize: 14 }}>
        No tienes permiso para ver Comparar Fotocasa v2.
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px 48px", maxWidth: 1280, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: tx, margin: "0 0 8px" }}>
        Comparar Fotocasa.es → import v2 (canónico)
      </h1>
      <p style={{ color: t.textMuted, fontSize: 14, margin: "0 0 20px", lineHeight: 1.5 }}>
        Importa una URL de fotocasa.es y revisa el modelo canónico. Abre el anuncio original con los botones de la
        derecha.
      </p>

      <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <label style={{ flex: "1 1 280px", display: "block" }}>
          <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>
            URL del anuncio (fotocasa.es)
          </span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.fotocasa.es/es/comprar/vivienda/…/123456789/d"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: `1px solid ${t.border}`,
              background: isDark ? "rgba(0,0,0,0.25)" : "#fff",
              color: tx,
              fontSize: 14,
            }}
          />
        </label>
        <button
          type="button"
          onClick={ejecutar}
          disabled={loading}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "none",
            fontWeight: 700,
            cursor: loading ? "wait" : "pointer",
            background: "linear-gradient(135deg, #f97316, #ea580c)",
            color: "#fff",
            opacity: loading ? 0.75 : 1,
          }}
        >
          {loading ? "Importando…" : "Importar y mostrar"}
        </button>
        <button
          type="button"
          onClick={() => setUrl(EJEMPLO_URL)}
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            border: `1px solid ${t.border}`,
            background: "transparent",
            color: t.textMuted,
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          URL ejemplo
        </button>
      </div>

      {err && (
        <div
          style={{
            ...card,
            borderColor: "rgba(239,68,68,0.5)",
            background: isDark ? "rgba(239,68,68,0.12)" : "#fef2f2",
            color: "#b91c1c",
          }}
        >
          {err}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 380px)",
          gap: 20,
          alignItems: "start",
        }}
        className="comparar-fotocasa-grid"
      >
        <style>{`
          @media (max-width: 960px) {
            .comparar-fotocasa-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>

        {/* Columna datos CRM */}
        <div>
          {data && (
            <>
              <div style={card}>
                <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>Resumen canónico</h2>
                <div style={{ display: "grid", gap: 8, fontSize: 14, color: tx }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 10,
                      fontSize: 13,
                      padding: 10,
                      borderRadius: 8,
                      background: isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
                    }}
                  >
                    <div>
                      <span style={{ color: t.textMuted }}>Publicación en portal</span>
                      <div style={{ fontWeight: 600 }}>
                        {fmtFechaHora(data.fecha_publicacion_portal || data.fecha_publicacion_portal_iso)}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Última actualización en portal</span>
                      <div style={{ fontWeight: 600 }}>
                        {fmtFechaHora(
                          data.fecha_ultima_actualizacion_portal
                            || data.fecha_ultima_actualizacion_portal_iso
                            || snap.fecha_actualizacion,
                        )}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Fecha entrega</span>
                      <div style={{ fontWeight: 600 }}>
                        {fmtValor(extras.fecha_entrega_portal || caraPortal.fecha_entrega)}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Primera importación CRM</span>
                      <div style={{ fontWeight: 600 }}>{fmtFechaHora(data.fecha_primera_importacion_crm)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Última importación CRM</span>
                      <div style={{ fontWeight: 600 }}>{fmtFechaHora(data.fecha_ultima_importacion_crm)}</div>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Título</span>
                    <div style={{ fontWeight: 600 }}>{tituloResumenLegible(snap)}</div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                    <div>
                      <span style={{ color: t.textMuted }}>Operación</span>
                      <div>{fmtValor(snap.operacion)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Referencia</span>
                      <div style={{ fontWeight: 600 }}>{fmtValor(snapReferencia)}</div>
                    </div>
                    {(snap.tipo_inmueble_nombre || snap.tipo_inmueble_id != null || extras.tipo_inmueble_nombre) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Tipo de propiedad</span>
                        <div>{fmtValor(snap.tipo_inmueble_nombre || extras.tipo_inmueble_nombre)}</div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Baños</span>
                      <div style={{ fontWeight: 600 }}>{fmtValor(snap.num_banos)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>m² construidos</span>
                      <div style={{ fontWeight: 600 }}>{fmtValor(snap.m2_construidos)}</div>
                    </div>
                    {(extras.m2_garaje_portal != null || extras.m2_despacho_portal != null || extras.m2_solarium_portal != null || extras.m2_sotano_portal != null || extras.m2_trastero_portal != null || extras.piscina_dimensiones_portal != null) && (
                      <>
                        {extras.m2_garaje_portal != null && (
                          <div>
                            <span style={{ color: t.textMuted }}>m² parking/garaje (descripción)</span>
                            <div style={{ fontWeight: 600 }}>{fmtValor(extras.m2_garaje_portal)}</div>
                          </div>
                        )}
                        {extras.m2_despacho_portal != null && (
                          <div>
                            <span style={{ color: t.textMuted }}>m² despacho (descripción)</span>
                            <div style={{ fontWeight: 600 }}>{fmtValor(extras.m2_despacho_portal)}</div>
                          </div>
                        )}
                        {extras.m2_solarium_portal != null && (
                          <div>
                            <span style={{ color: t.textMuted }}>m² solárium (descripción)</span>
                            <div style={{ fontWeight: 600 }}>{fmtValor(extras.m2_solarium_portal)}</div>
                          </div>
                        )}
                        {extras.m2_sotano_portal != null && (
                          <div>
                            <span style={{ color: t.textMuted }}>m² sótano (descripción)</span>
                            <div style={{ fontWeight: 600 }}>{fmtValor(extras.m2_sotano_portal)}</div>
                          </div>
                        )}
                        {extras.m2_trastero_portal != null && (
                          <div>
                            <span style={{ color: t.textMuted }}>m² trastero (descripción)</span>
                            <div style={{ fontWeight: 600 }}>{fmtValor(extras.m2_trastero_portal)}</div>
                          </div>
                        )}
                        {extras.piscina_dimensiones_portal != null && (
                          <div>
                            <span style={{ color: t.textMuted }}>Piscina (dimensiones)</span>
                            <div style={{ fontWeight: 600 }}>
                              {extras.piscina_dimensiones_portal}
                              {extras.piscina_m2_portal != null ? ` (${fmtValor(extras.piscina_m2_portal)} m²)` : ''}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {m2ParcelaResuelto != null && (
                      <div>
                        <span style={{ color: t.textMuted }}>Terreno / parcela</span>
                        <div style={{ fontWeight: 600 }}>{fmtValor(m2ParcelaResuelto)} m²</div>
                      </div>
                    )}
                    {(snap.poblacion_nombre ||
                      snap.poblacion_id != null ||
                      extras.poblacion_texto_sin_id) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Población</span>
                        <div>
                          {snap.poblacion_nombre || extras.poblacion_texto_sin_id
                            ? fmtValor(snap.poblacion_nombre || extras.poblacion_texto_sin_id)
                            : "—"}
                        </div>
                      </div>
                    )}
                    {(snap.orientacion_nombre || snap.orientacion_id != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Orientación</span>
                        <div style={{ fontWeight: 600 }}>
                          {snap.orientacion_nombre ? fmtValor(snap.orientacion_nombre) : "—"}
                        </div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Tipo anunciante</span>
                      <div style={{ fontWeight: 700 }}>{fmtValor(snap.tipo_anunciante)}</div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Precio venta</span>
                      <div style={{ fontWeight: 700 }}>{fmtPrecio(snap.precio_venta)}</div>
                    </div>
                    {(snap.precio_rebajado === true || snap.bajada_precio_euros != null || extras.bajada_precio_texto) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Bajada de precio</span>
                        <div style={{ fontWeight: 600 }}>
                          {extras.bajada_precio_texto
                            || (snap.bajada_precio_euros != null
                              ? `ha bajado ${fmtPrecio(snap.bajada_precio_euros).replace(" €", " €")}`
                              : "Precio rebajado")}
                        </div>
                      </div>
                    )}
                    {(snap.web_url || data?.url_origen) && (
                      <div style={{ flex: "1 1 100%" }}>
                        <span style={{ color: t.textMuted }}>URL anuncio</span>
                        <div style={{ fontSize: 12, wordBreak: "break-all" }}>
                          {snap.web_url || data?.url_origen}
                        </div>
                      </div>
                    )}
                    {snap.exterior != null && (
                      <div>
                        <span style={{ color: t.textMuted }}>Exterior</span>
                        <div style={{ fontWeight: 600 }}>{fmtValor(snap.exterior)}</div>
                      </div>
                    )}
                    {(snap.cal_energetica || snap.cal_energetica_consumo != null || snap.cal_energetica_emisiones != null) && (
                      <div style={{ flex: "1 1 100%" }}>
                        <span style={{ color: t.textMuted }}>Certificado energético</span>
                        <div style={{ fontSize: 13 }}>
                          {[
                            snap.cal_energetica ? `Letra ${snap.cal_energetica}` : null,
                            snap.cal_energetica_consumo != null ? `Consumo ${snap.cal_energetica_consumo} kWh/m²/año` : null,
                            snap.cal_energetica_emisiones != null ? `Emisiones ${snap.cal_energetica_emisiones} kg CO₂/m²/año` : null,
                          ].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Jardín / m² jardín</span>
                      <div>
                        {extras.jardin === false
                          ? "No (ficha portal)"
                          : extras.jardin === true ||
                              snap.m2_jardin != null ||
                              snap.m2_jardin_obs
                            ? [
                                extras.jardin === true || snap.m2_jardin != null || snap.m2_jardin_obs ? "Sí" : null,
                                snap.m2_jardin != null ? `${snap.m2_jardin} m²` : null,
                                snap.m2_jardin_obs || null,
                              ]
                                .filter(Boolean)
                                .join(" · ") || "—"
                            : "—"}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: t.textMuted }}>Teléfono</span>
                      <div style={{ fontWeight: 600 }}>
                        {telefonoResuelto
                          ? fmtValor(telefonoResuelto)
                          : extras.telefono_anuncio
                            ? fmtValor(extras.telefono_anuncio)
                            : "—"}
                      </div>
                    </div>
                    {(snap.eq_barbacoa != null ||
                      snap.eq_lavadero != null ||
                      caraPortal.barbacoa === true ||
                      caraPortal.lavadero === true) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Barbacoa / lavadero</span>
                        <div>
                          Barbacoa {fmtValor(snap.eq_barbacoa)} · lavadero {fmtValor(snap.eq_lavadero)}
                        </div>
                      </div>
                    )}
                    {(extras.acceso_playa_directo_portal === true || snap.ub_primera_linea != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Ubicación playa</span>
                        <div style={{ fontWeight: 600 }}>
                          {[
                            extras.acceso_playa_directo_portal ? "Acceso directo a la playa" : null,
                            snap.ub_primera_linea ? "Primera línea" : null,
                          ].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    )}
                    {snap.num_terrazas != null && (
                      <div>
                        <span style={{ color: t.textMuted }}>Terrazas</span>
                        <div style={{ fontWeight: 600 }}>
                          {[
                            snap.num_terrazas != null ? `${snap.num_terrazas}` : null,
                            snap.m2_terrazas != null ? `${snap.m2_terrazas} m²` : null,
                          ].filter(Boolean).join(" · ") || fmtValor(snap.num_terrazas)}
                        </div>
                      </div>
                    )}
                    {snap.ub_vistas_id != null && (
                      <div>
                        <span style={{ color: t.textMuted }}>Vistas</span>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {extras.ub_vistas_nombre
                            ? fmtValor(extras.ub_vistas_nombre)
                            : String(snap.ub_vistas_id)}
                        </div>
                      </div>
                    )}
                    <div>
                      <span style={{ color: t.textMuted }}>Dorm. / baños</span>
                      <div>
                        {fmtValor(snap.num_dormitorios)} / {fmtValor(snap.num_banos)}
                      </div>
                    </div>
                    {(snap.plantas_inmueble != null || snap.altura_planta != null || snap.piso) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Plantas inmueble / planta</span>
                        <div>
                          {[
                            snap.plantas_inmueble != null ? `${snap.plantas_inmueble} planta(s)` : null,
                            snap.altura_planta != null ? `altura ${snap.altura_planta}` : null,
                            snap.piso || null,
                          ].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    )}
                    {(snap.eq_agua_riego != null || extras.deposito_agua_litros_portal != null) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Agua de riego / depósito</span>
                        <div style={{ fontWeight: 600 }}>
                          {[
                            snap.eq_agua_riego === true ? "Sí" : snap.eq_agua_riego === false ? "No" : null,
                            extras.deposito_agua_litros_portal != null
                              ? `${extras.deposito_agua_litros_portal.toLocaleString("es-ES")} L`
                              : null,
                          ].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    )}
                    {extras.cocina_verano_portal === true && (
                      <div>
                        <span style={{ color: t.textMuted }}>Cocina de verano</span>
                        <div style={{ fontWeight: 600 }}>Sí</div>
                      </div>
                    )}
                    {extras.cerca_transporte_publico_portal === true && (
                      <div>
                        <span style={{ color: t.textMuted }}>Transporte público</span>
                        <div style={{ fontWeight: 600 }}>Cerca de transporte público</div>
                      </div>
                    )}
                    {(extras.promocion_padre_nombre_portal || extras.promocion_padre_url_portal) && (
                      <div style={{ flex: "1 1 100%" }}>
                        <span style={{ color: t.textMuted }}>Promoción obra nueva</span>
                        <div style={{ fontWeight: 600 }}>
                          {extras.promocion_padre_nombre_portal || "—"}
                          {extras.promocion_num_tipos_portal != null
                            ? ` (${extras.promocion_num_tipos_portal} tipos)`
                            : ""}
                        </div>
                        {extras.promocion_padre_url_portal && (
                          <div style={{ fontSize: 12, wordBreak: "break-all", marginTop: 4 }}>
                            {extras.promocion_padre_url_portal}
                          </div>
                        )}
                      </div>
                    )}
                    {(extras.sistema_alarma_portal === true || extras.doble_acristalamiento_portal === true) && (
                      <div>
                        <span style={{ color: t.textMuted }}>Extras (destacadas)</span>
                        <div style={{ fontWeight: 600 }}>
                          {[
                            extras.sistema_alarma_portal ? "Sistema de alarma" : null,
                            extras.doble_acristalamiento_portal ? "Doble acristalamiento" : null,
                          ].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    )}
                    {snap.eq_ascensor === true || snap.eq_ascensor === false ? (
                      <div>
                        <span style={{ color: t.textMuted }}>Ascensor</span>
                        <div>{snap.eq_ascensor ? "Sí" : "No"}</div>
                      </div>
                    ) : null}
                    <div>
                      <span style={{ color: t.textMuted }}>m² út. / const. / parcela</span>
                      <div>
                        {fmtValor(snap.m2_utiles)} / {fmtValor(snap.m2_construidos)} /{" "}
                        {fmtValor(snap.m2_parcela)}
                      </div>
                    </div>
                    {extras.tipo_cocina_portal && (
                      <div>
                        <span style={{ color: t.textMuted }}>Tipo cocina</span>
                        <div style={{ fontWeight: 600 }}>{fmtValor(extras.tipo_cocina_portal)}</div>
                      </div>
                    )}
                    {(piscinaResuelta || garajeTiene || garajeNo || aireTiene || aireNo || calefTiene || calefNo ||
                      snap.eq_piscina_priv != null ||
                      snap.eq_piscina_comun != null ||
                      snap.eq_garaje != null ||
                      snap.eq_calefaccion != null ||
                      snap.eq_aire != null) && (
                      <div style={{ flexBasis: "100%" }}>
                        <span style={{ color: t.textMuted }}>Equipamiento</span>
                        <div style={{ fontSize: 13 }}>
                          {[
                            piscinaResuelta || snap.eq_piscina_priv != null || snap.eq_piscina_comun != null
                              ? `Piscina ${
                                  piscinaResuelta || snap.eq_piscina_priv || snap.eq_piscina_comun ? "Sí" : "No"
                                }`
                              : null,
                            garajeTiene || garajeNo || snap.eq_garaje != null || caraPortal.garaje != null
                              ? `Parking / garaje ${garajeTiene ? "Sí" : "No"}`
                              : null,
                            calefTiene || calefNo || snap.eq_calefaccion != null || caraPortal.calefaccion != null
                              ? `Calefacción ${calefTiene ? "Sí" : "No"}`
                              : null,
                            aireTiene || aireNo || snap.eq_aire != null || caraPortal.aire_acondicionado != null
                              ? `Aire ${aireTiene ? "Sí" : "No"}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Contacto</span>
                    <div style={{ fontWeight: 600 }}>
                      {fmtValor(snap.alias ?? extras.contacto_display)}
                    </div>
                    {(extras.contacto_nombre || extras.contacto_apellidos) && (
                      <div style={{ fontSize: 13, marginTop: 4 }}>
                        <span style={{ color: t.textMuted }}>Nombre</span>
                        <div>
                          {fmtValor(extras.contacto_nombre)} {fmtValor(extras.contacto_apellidos)}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>Dirección</span>
                    <div style={{ fontSize: 13 }}>
                      {extras.direccion_completa_portal
                        || [
                          snap.nombre_via,
                          snap.numero ? `nº ${snap.numero}` : null,
                          snap.poblacion_nombre || extras.poblacion_texto_sin_id || extras.municipio_portal,
                          snap.codigo_postal ? `CP ${snap.codigo_postal}` : null,
                        ]
                          .filter(Boolean)
                          .join(", ")
                        || "—"}
                    </div>
                    {extras.ubicacion_aprox_portal && (
                      <div style={{ marginTop: 6, fontSize: 12, color: t.textMuted }}>
                        Zona: {extras.ubicacion_aprox_portal}
                      </div>
                    )}
                  </div>
                  <div>
                    <span style={{ color: t.textMuted }}>
                      Descripción · {descLen.toLocaleString("es-ES")} caracteres
                    </span>
                    <div
                      style={{
                        maxHeight: "min(75vh, 900px)",
                        overflow: "auto",
                        lineHeight: 1.45,
                        fontSize: 13,
                        padding: 10,
                        borderRadius: 8,
                        background: isDark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {snap.web_desc_larga_es || "—"}
                    </div>
                  </div>
                </div>
              </div>

              <div style={card}>
                <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>Snapshot (campos importados)</h2>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>
                        <th style={{ padding: "8px 6px" }}>Campo</th>
                        <th style={{ padding: "8px 6px" }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapKeys.map((k) => (
                        <tr key={k} style={{ borderBottom: `1px solid ${t.border}` }}>
                          <td style={{ padding: "8px 6px", fontFamily: "monospace", color: t.accentBlue }}>
                            {k}
                          </td>
                          <td style={{ padding: "8px 6px", color: tx, wordBreak: "break-word" }}>
                            {k === "ub_vistas_id" && extras.ub_vistas_nombre ? (
                              <div style={{ fontWeight: 600 }}>{fmtValor(extras.ub_vistas_nombre)}</div>
                            ) : k === "orientacion_id" && snap.orientacion_nombre ? (
                              <div style={{ fontWeight: 600 }}>{fmtValor(snap.orientacion_nombre)}</div>
                            ) : (
                              fmtValorSnapshotCampo(k, snap, extras, snapReferencia, telefonoResuelto)
                            )}
                          </td>
                        </tr>
                      ))}
                      {FOTOCASA_PORTAL_EXTRAS_ROWS.map((k) => (
                        <tr key={`extra:${k}`} style={{ borderBottom: `1px solid ${t.border}` }}>
                          <td style={{ padding: "8px 6px", fontFamily: "monospace", color: t.accentBlue }}>
                            portal_extras.{k}
                          </td>
                          <td style={{ padding: "8px 6px", color: tx, wordBreak: "break-word" }}>
                            {fmtValor(extras[k])}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <details style={card}>
                <summary style={{ cursor: "pointer", fontWeight: 600, color: tx }}>
                  Características crudas del portal (JSON)
                </summary>
                <pre
                  style={{
                    marginTop: 12,
                    fontSize: 11,
                    overflow: "auto",
                    maxHeight: 280,
                    color: tx,
                    background: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  {JSON.stringify(caraPortal, null, 2)}
                </pre>
              </details>

              <details style={card}>
                <summary style={{ cursor: "pointer", fontWeight: 600, color: tx }}>
                  Extras portal (contacto, teléfono, etc.)
                </summary>
                <pre
                  style={{
                    marginTop: 12,
                    fontSize: 11,
                    overflow: "auto",
                    maxHeight: 220,
                    color: tx,
                    background: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  {JSON.stringify(
                    { ...extras, caracteristicas_portal: "(arriba)" },
                    null,
                    2
                  )}
                </pre>
              </details>

              {videos.length > 0 && (
                <div style={card}>
                  <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>
                    Vídeos / tour virtual ({videos.length})
                  </h2>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {videos.map((src, i) => {
                      const thumb = youtubeThumb(src);
                      return (
                        <a
                          key={i}
                          href={src}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "block",
                            borderRadius: 8,
                            overflow: "hidden",
                            border: `1px solid ${t.border}`,
                            textDecoration: "none",
                            color: tx,
                          }}
                        >
                          {thumb ? (
                            <img
                              src={thumb}
                              alt=""
                              style={{ width: "100%", height: 112, objectFit: "cover", display: "block" }}
                              loading="lazy"
                            />
                          ) : (
                            <div
                              style={{
                                height: 112,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: t.surfaceMuted || "#f4f4f5",
                                fontSize: 13,
                                padding: 8,
                                textAlign: "center",
                              }}
                            >
                              Abrir vídeo
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: 11,
                              padding: "6px 8px",
                              wordBreak: "break-all",
                              color: t.textMuted,
                            }}
                          >
                            {src.replace(/^https?:\/\//, "")}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {fotos.length > 0 && (
                <div style={card}>
                  <h2 style={{ fontSize: 16, margin: "0 0 12px", color: tx }}>
                    Fotos ({fotos.length})
                  </h2>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {fotos.slice(0, FOTOS_UI_MAX).map((src, i) => (
                      <a
                        key={i}
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "block", borderRadius: 8, overflow: "hidden", border: `1px solid ${t.border}` }}
                      >
                        <img
                          src={proxyFoto(src)}
                          alt=""
                          style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }}
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                  {fotos.length > FOTOS_UI_MAX && (
                    <p style={{ fontSize: 12, color: t.textMuted, margin: "10px 0 0" }}>
                      Mostrando {FOTOS_UI_MAX} de {fotos.length}; el scraping guarda como máximo {FOTOS_UI_MAX} por anuncio.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Columna anuncio original */}
        <div style={{ position: "sticky", top: 16 }}>
          <div style={card}>
            <h2 style={{ fontSize: 16, margin: "0 0 10px", color: tx }}>Anuncio original</h2>
            <p style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.5, margin: "0 0 14px" }}>
              Coloca esta página a la izquierda y la ventana del portal a la derecha, o usa dos monitores.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                type="button"
                onClick={abrirOriginalNuevaPestaña}
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: "none",
                  fontWeight: 700,
                  cursor: "pointer",
                  background: "#ea580c",
                  color: "#fff",
                }}
              >
                Abrir Fotocasa en nueva pestaña
              </button>
              <button
                type="button"
                onClick={abrirOriginalPopup}
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: `1px solid ${t.border}`,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent",
                  color: tx,
                }}
              >
                Abrir en ventana flotante
              </button>
            </div>
            {(data?.url_origen || url) && (
              <p
                style={{
                  fontSize: 11,
                  wordBreak: "break-all",
                  marginTop: 14,
                  color: t.textMuted,
                  fontFamily: "monospace",
                }}
              >
                {data?.url_origen || url}
              </p>
            )}
          </div>

          {data && (
            <div style={{ ...card, fontSize: 12, color: t.textMuted }}>
              <div>
                <strong style={{ color: tx }}>ID import:</strong> {data.id}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>ID anuncio:</strong> {data.fotocasa_ad_id ?? "—"}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>referencia:</strong> {fmtValor(snapReferencia)}
              </div>
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>teléfono:</strong> {fmtValor(telefonoResuelto)}
              </div>
              {data.scrape_meta && (
                <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 11, wordBreak: "break-word" }}>
                  <strong style={{ color: tx }}>scrape_meta:</strong> {JSON.stringify(data.scrape_meta)}
                </div>
              )}
              <div style={{ marginTop: 6 }}>
                <strong style={{ color: tx }}>Actualizado:</strong>{" "}
                {data.updated_at ? new Date(data.updated_at).toLocaleString("es-ES") : "—"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
